import api from './api';
import type { Menu, MenuRenderResponse, MenuStatus, MenuType, PaginatedResponse } from '../types/menu';

const base = (venueId: string) => `/admin/venue/${venueId}/menu`;

const menuService = {
  /** Get all menus for a venue, paginated. */
  getMenus: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    type?: MenuType,
    status?: MenuStatus | 'all',
  ): Promise<PaginatedResponse<Menu>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (type) params.type = type;
    if (status) params.status = status;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /** Get a single menu. */
  getMenu: async (venueId: string, menuId: string): Promise<Menu> => {
    const response = await api.get(`${base(venueId)}/${menuId}`);
    return response.data;
  },

  /** Create a menu. */
  createMenu: async (
    venueId: string,
    data: Omit<Menu, '_id' | 'venueId' | 'createdAt' | 'updatedAt'>,
  ): Promise<Menu> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  /** Update a menu. */
  updateMenu: async (
    venueId: string,
    menuId: string,
    data: Partial<Omit<Menu, '_id' | 'venueId' | 'createdAt' | 'updatedAt'>>,
  ): Promise<Menu> => {
    const response = await api.put(`${base(venueId)}/${menuId}`, data);
    return response.data;
  },

  /** Archive (soft-delete) a menu. */
  deleteMenu: async (venueId: string, menuId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${menuId}`);
  },

  /** Admin render DTO for a menu (cocktail items only in Phase 2A.1). */
  renderMenu: async (venueId: string, menuId: string): Promise<MenuRenderResponse> => {
    const response = await api.get(`${base(venueId)}/${menuId}/render`);
    return response.data;
  },
};

export default menuService;
